import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Loader2, User, Clock, 
  Phone, Check, CheckCheck, AlertCircle, RefreshCw,
  FileText, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SMSComposer = ({ isOpen, onClose, contact, onSmsSent }) => {
  const { token } = useAuth();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [smsStatus, setSmsStatus] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && contact) {
      fetchConversation();
      fetchTemplates();
      checkSmsStatus();
    }
  }, [isOpen, contact]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const checkSmsStatus = async () => {
    try {
      const response = await axios.get(`${API}/sms/status?token=${token}`);
      setSmsStatus(response.data);
    } catch (error) {
      console.error('Failed to check SMS status', error);
    }
  };

  const fetchConversation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/sms/conversation/${contact.id}?token=${token}`);
      setConversation(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch conversation', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/sms/templates?token=${token}`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates', error);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Replace placeholders with contact data
      let text = template.message;
      text = text.replace('{{name}}', contact.name || 'there');
      text = text.replace('{{company}}', contact.company || 'your company');
      setMessage(text);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !contact?.phone || sending) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/sms/quick-send?token=${token}`, {
        contact_id: contact.id,
        message: message.trim()
      });

      if (response.data.success) {
        toast.success('SMS sent successfully');
        setMessage('');
        fetchConversation();
        if (onSmsSent) onSmsSent();
      } else {
        throw new Error(response.data.error || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('SMS send error', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { timeZone: 'Asia/Dubai', hour: 'numeric', minute: '2-digit' });
    }
    
    return date.toLocaleDateString('en-US', { 
      timeZone: 'Asia/Dubai',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCheck size={12} className="text-blue-500" />;
      case 'sent':
        return <Check size={12} className="text-slate-400" />;
      case 'failed':
        return <AlertCircle size={12} className="text-red-500" />;
      case 'received':
        return null;
      default:
        return <Clock size={12} className="text-slate-300" />;
    }
  };

  if (!contact) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg h-[600px] flex flex-col p-0" data-testid="sms-composer-dialog">
        {/* Header */}
        <DialogHeader className="p-4 pb-2 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <MessageSquare size={18} className="text-green-600" />
              </div>
              <div>
                <DialogTitle className="text-base">{contact.name || 'Unknown Contact'}</DialogTitle>
                <DialogDescription className="text-xs flex items-center gap-1">
                  <Phone size={10} />
                  {contact.phone || 'No phone number'}
                </DialogDescription>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X size={18} />
            </Button>
          </div>
        </DialogHeader>

        {/* SMS Status Warning */}
        {smsStatus && !smsStatus.configured && (
          <div className="mx-4 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-700">
              <AlertCircle size={12} className="inline mr-1" />
              SMS service not configured. Add Twilio credentials in Settings.
            </p>
          </div>
        )}

        {/* No Phone Warning */}
        {!contact.phone && (
          <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-700">
              <AlertCircle size={12} className="inline mr-1" />
              This contact has no phone number.
            </p>
          </div>
        )}

        {/* Conversation */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="animate-spin text-slate-400" size={24} />
            </div>
          ) : conversation.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageSquare size={40} className="text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No messages yet</p>
              <p className="text-xs text-slate-400">Start the conversation below</p>
            </div>
          ) : (
            conversation.map((msg, idx) => {
              const isOutbound = msg.direction === 'outbound';
              return (
                <div 
                  key={idx} 
                  className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${isOutbound ? 'order-2' : 'order-1'}`}>
                    <div className={`rounded-2xl px-4 py-2 ${
                      isOutbound 
                        ? 'bg-green-500 text-white rounded-br-md' 
                        : 'bg-white border shadow-sm rounded-bl-md'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                    </div>
                    <div className={`flex items-center gap-1 mt-1 ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                      <span className="text-xs text-slate-400">
                        {formatTimestamp(msg.created_at)}
                      </span>
                      {isOutbound && getStatusIcon(msg.status)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Templates */}
        {templates.length > 0 && (
          <div className="px-4 py-2 border-t bg-white">
            <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
              <SelectTrigger className="h-8 text-xs">
                <FileText size={12} className="mr-2" />
                <SelectValue placeholder="Use a template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-xs">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              disabled={!contact.phone || !smsStatus?.configured || sending}
              className="resize-none min-h-[60px] text-sm"
              rows={2}
              data-testid="sms-message-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!message.trim() || !contact.phone || !smsStatus?.configured || sending}
              className="bg-green-500 hover:bg-green-600 self-end"
              data-testid="send-sms-btn"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SMSComposer;
